import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Layout,
  theme,
  Tabs,
  Row,
  Col,
  Card,
  Button,
  Input,
  Modal,
  message,
} from "antd";
import {
  TagOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  BookOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import type { TabsProps } from "antd";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import TopBar from "../../components/TopBar";
import DocumentAuditMetaBlock from "../../components/DocumentList/DocumentAuditMetaBlock";
import PDFPreview from "../../components/PdfPreview";
import SideBar from "../../components/SideBar";
import { ClaimAuditTable, ClaimNoteTable } from "../../components/Table";
import {
  ApproveClaimModal,
  RejectClaimModal,
  PaidClaimModal,
  CloseClaimModal,
  AddClaimDocumentModal,
} from "../../components/ModalForm";

import { AuthContext } from "../../context/AuthContext";

import { downloadDocumentApi } from "../../services/Document/DocumentApis";
import {
  getClaimApi,
  addClaimNoteApi,
  approveClaimApi,
  rejectClaimApi,
  paidClaimApi,
  closeClaimApi,
  addClaimDocumentApi,
} from "../../services/Claim/ClaimApis";
import { getUserApi } from "../../services/User/UserApis";
import { invalidateCompliance } from "../../utils/complianceInvalidate";
import {
  enrichDocumentsWithKnownUsers,
  getDocumentAuditMeta,
} from "../../utils/documentAudit";

const { Content } = Layout;

const ClaimManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const { claimId }: any = useParams();
  const { currentUser } = useContext(AuthContext);

  const [state, setState] = useState({
    approve: false,
    reject: false,
    paid: false,
    close: false,
  });
  const [claim, setClaim] = useState<any>({});
  const [documents, setDocuments] = useState<any>([]);
  const [audits, setAudits] = useState<any>([]);
  const [notes, setNotes] = useState<any>([]);
  const [isActive, setActive] = useState(true);
  const [claimStatus, setClaimStatus] = useState();
  const [isInWaitingPeriod, setIsInWaitingPeriod] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [showUploadDocs, setShowUploadDocs] = useState(false);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const onChange = (key: string) => {
    console.log(key);
  };

  const applyDocuments = async (docs: any[], auditRows?: any[]) => {
    const knownUsers = [
      currentUser,
      ...(Array.isArray(auditRows) ? auditRows : audits).map(
        (a: any) => a?.User
      ),
    ].filter(Boolean);

    let enriched = enrichDocumentsWithKnownUsers(docs || [], knownUsers);

    // Claim GET often omits UploadedBy.Name — resolve via GET /user/:id
    const missingIds = Array.from(
      new Set(
        enriched
          .map((doc) => getDocumentAuditMeta(doc))
          .filter(
            (meta) =>
              meta.uploadedById != null &&
              String(meta.uploaderName ?? "").startsWith("User #")
          )
          .map((meta) => String(meta.uploadedById))
      )
    );

    if (missingIds.length > 0) {
      const fetched = await Promise.all(
        missingIds.map((id) =>
          getUserApi(id)
            .then((res) => res?.data)
            .catch(() => null)
        )
      );
      enriched = enrichDocumentsWithKnownUsers(
        enriched,
        fetched.filter(Boolean)
      );
    }

    setDocuments(enriched);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleOpen = () => {
    setIsModalOpen(true);
    setNoteContent("");
  };

  const handleAddNote = async (claimId: any, data: any) => {
    setActive(true);

    return await addClaimNoteApi(claimId, { Notes: data }, currentUser.id)
      .then((res) => {
        setActive(false);
        setNotes([...notes, res.data]);
        setIsModalOpen(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        setIsModalOpen(false);
        console.log(err);
      });
  };

  const showApproveModal = () => {
    setState((prevState) => ({
      approve: !prevState.approve,
      reject: prevState.reject,
      paid: prevState.paid,
      close: prevState.close,
    }));
  };

  const showRejectModal = () => {
    setState((prevState) => ({
      approve: prevState.approve,
      reject: !prevState.reject,
      paid: prevState.paid,
      close: prevState.close,
    }));
  };

  const showPaidModal = () => {
    setState((prevState) => ({
      approve: prevState.approve,
      reject: prevState.reject,
      paid: !prevState.paid,
      close: prevState.close,
    }));
  };

  const showCloseModal = () => {
    setState((prevState) => ({
      approve: prevState.approve,
      reject: prevState.reject,
      paid: prevState.paid,
      close: !prevState.close,
    }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadDocument = async (data: any) => {
    if (!data?.Link) {
      message.error("This document has no download link.");
      return;
    }

    setActive(true);

    await downloadDocumentApi({ Link: data.Link })
      .then(async (res) => {
        const presignedUrl = res.data;
        if (!presignedUrl || typeof presignedUrl !== "string") {
          throw new Error("No download URL returned from server.");
        }
        if (
          String(presignedUrl).includes("AccessDenied") ||
          String(presignedUrl).includes("<Error>")
        ) {
          throw new Error(
            "Storage denied access to this file. Claim documents may need a backend fix for the image/document bucket."
          );
        }
        const filename =
          (data.Name ? `${data.Name}` : null) ||
          (data.Link || "").split("/").pop() ||
          `document_${Date.now()}`;

        const fileRes = await fetch(presignedUrl);
        if (!fileRes.ok) {
          const errText = await fileRes.text().catch(() => "");
          if (errText.includes("AccessDenied") || fileRes.status === 403) {
            throw new Error(
              "S3 AccessDenied — the file is on a bucket the download service cannot read (often life-cycle-image-bucket). Backend must presign claim docs correctly."
            );
          }
          throw new Error(
            `Document fetch failed: ${fileRes.status} ${fileRes.statusText}`
          );
        }
        const blob = await fileRes.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        message.success("Document has started downloading.");
        setActive(false);
      })
      .catch((err) => {
        setActive(false);
        console.error(err);
        message.error(
          err?.message || "Error occurred trying to download the document."
        );
      });
  };

  const refreshClaim = async () => {
    const res = await getClaimApi(String(claimId));
    setClaim(res.data);
    const auditRows = res.data?.ClaimAudit ?? res.data?.ClaimAuditTrail ?? [];
    setAudits(auditRows);
    await applyDocuments(res.data?.ClaimDocument ?? [], auditRows);
    setNotes(res.data?.ClaimNotes ?? []);
    setClaimStatus(res.data?.ClaimStatus ?? res.data?.Status);
    return res;
  };

  const handleAddClaimDocuments = async (
    docs: { Name: string; Value: string }[]
  ) => {
    setActive(true);
    try {
      const response = await addClaimDocumentApi(String(claimId), {
        Documents: docs,
      });
      const failed =
        response?.success === false ||
        (typeof response?.status === "number" && response.status >= 400);

      if (failed) {
        const msg =
          response?.message ||
          response?.error ||
          "Unable to upload claim documents.";
        message.error(msg);
        return { success: false, message: msg };
      }

      if (Array.isArray(response?.data)) {
        await applyDocuments(response.data);
      } else {
        await refreshClaim();
      }
      invalidateCompliance();
      message.success("Claim documents uploaded.");
      return response ?? { success: true };
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to upload claim documents."
      );
      return { success: false, message: err?.message };
    } finally {
      setActive(false);
    }
  };

  const handleApproveClaim = async (data: any, claimId: any) => {
    setActive(true);

    return await approveClaimApi(claimId, { ...data, ...currentUser })
      .then((res) => {
        setAudits([...audits, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleRejectClaim = async (data: any, claimId: any) => {
    setActive(true);

    return await rejectClaimApi(claimId, { ...data, ...currentUser })
      .then((res) => {
        setAudits([...audits, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handlePaidClaim = async (data: any, claimId: any) => {
    setActive(true);

    return await paidClaimApi(claimId, { ...data, ...currentUser })
      .then((res) => {
        setAudits([...audits, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleCloseClaim = async (data: any, claimId: any) => {
    setActive(true);

    return await closeClaimApi(claimId, { ...data, ...currentUser })
      .then((res) => {
        setAudits([...audits, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: `Claim Info`,
      children: (
        <div className="content-border">
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Basic Info"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Beneficiary Name:</h4>
                  <span> {claim.BenName}</span>
                </div>
                <div className="card-content">
                  <h4>Beneficiary Surname:</h4>
                  <span>{claim.BenSurname}</span>
                </div>
                <div className="card-content">
                  <h4>Beneficiary ID Number :</h4>
                  <span> {claim.BenIdNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Claim Amount:</h4>
                  <span> {claim.ClaimAmount}</span>
                </div>
                <div className="card-content">
                  <h4>Claim Date :</h4>
                  <span> {dayjs(claim.Date).format("YYYY-MM-DD")}</span>
                </div>
                <div className="card-content">
                  <h4>Claim Status:</h4>
                  <span> {claim.Status}</span>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="Deceased Info"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Deceased Name :</h4>
                  <span> {claim.DeceasedName}</span>
                </div>
                <div className="card-content">
                  <h4>Deceased Surname :</h4>
                  <span> {claim.DeceasedSurname}</span>
                </div>
                <div className="card-content">
                  <h4>Deceased ID Number:</h4>
                  <span> {claim.DeceasedIdNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Deceased Type:</h4>
                  <span> {claim.DeceasedType}</span>
                </div>
                <div className="card-content">
                  <h4>Date Of Death :</h4>
                  <span> {dayjs(claim.DateOfDeath).format("YYYY-MM-DD")}</span>
                </div>
                <div className="card-content">
                  <h4>Cause Of Death:</h4>
                  <span> {claim.CauseOfDeath}</span>
                </div>
                {(claim.Notes || claim.AdditionalNotes) && (
                  <div className="card-content">
                    <h4>Notes:</h4>
                    <span> {claim.Notes || claim.AdditionalNotes}</span>
                  </div>
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="Payment Info"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Bank:</h4>
                  <span>{claim.Bank}</span>
                </div>
                <div className="card-content">
                  <h4>Account Number :</h4>
                  <span>{claim.AccountNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Account Type:</h4>
                  <span>{claim.AccountType}</span>
                </div>
                <div className="card-content">
                  <h4>Branch Code:</h4>
                  <span>{claim.BranchCode}</span>
                </div>
                <div className="card-content">
                  <h4>Amount:</h4>
                  <span>R {claim.ClaimAmount}</span>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="Policy Info"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Policy Number :</h4>
                  <span> {claim.Policy?.PolicyNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Oustanding Premium :</h4>
                  <span>R {claim.Policy?.OutstandingPremium}</span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "2",
      label: `Claim Documents`,
      children: (
        <div className="content-border">
          <Row gutter={[12, 12]} className="document-container">
            {(documents || []).map((document: any) => (
              <Col
                xs={24}
                sm={12}
                md={8}
                lg={6}
                key={document.id ?? document.Name}
              >
                <div className="document-item">
                  <h5 title={document.Name}>{document.Name}</h5>
                  <DocumentAuditMetaBlock document={document} />
                  <div
                    style={{
                      width: "100%",
                      flex: 1,
                      minHeight: 120,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <PDFPreview pdfUrl={document?.Link} />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    <DownloadOutlined
                      className="document-edit"
                      onClick={() => handleDownloadDocument(document)}
                      title="Download"
                    />
                  </div>
                </div>
              </Col>
            ))}
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={6}
              onClick={() => setShowUploadDocs(true)}
            >
              <div className="document-item-add" title="Add new document">
                <FileAddOutlined className="document-icon" />
              </div>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "3",
      label: `Manage Claim`,
      children: (
        <>
          <div className="content-border">
            <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
              <Col span={3}>
                <h4>Claim Status:</h4>
                <span>{claimStatus}</span>
              </Col>
              <Col span={3}>
                <h4>Claim Amount:</h4>
                <span>R {claim.ClaimAmount}</span>
              </Col>
              <Col span={4}>
                <h4>Policy Status:</h4>
                {isInWaitingPeriod ? (
                  <p style={{ color: "#bd4f4f" }}>
                    The policy is in waiting period because of outstanding
                    payments.
                  </p>
                ) : (
                  <p style={{ color: "#5aa665" }}>
                    The policy is all paid up and out of waiting period
                  </p>
                )}
              </Col>
              <Col span={14}>
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Button className="claim-continue-btn" size="large" block onClick={showApproveModal}>
                      <CheckCircleOutlined />
                      Approve
                    </Button>
                  </Col>
                  <Col span={6}>
                    <Button className="claim-reject-btn" size="large" block onClick={showRejectModal}>
                      <MinusCircleOutlined /> Reject
                    </Button>
                  </Col>
                  <Col span={6}>
                    <Button className="claim-paid-btn" size="large" block onClick={showPaidModal}>
                      <DollarOutlined /> Paid
                    </Button>
                  </Col>
                  <Col span={6}>
                    <Button className="claim-closed-btn" size="large" block onClick={showCloseModal}>
                      <CloseCircleOutlined /> Closed
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <h3 style={{ padding: "24px 0" }}>Claim Audit Trail</h3>
          <ClaimAuditTable audits={audits} />
          <h3 style={{ padding: "24px 0" }}>Claim Notes</h3>
          <ClaimNoteTable notes={notes} />
        </>
      ),
    },
  ];

  useEffect(() => {
    getClaimApi(String(claimId))
      .then(async (res) => {
        const auditRows =
          res.data.ClaimAuditTrail ?? res.data.ClaimAudit ?? [];
        await applyDocuments(res.data.ClaimDocument, auditRows);
        setClaim(res.data);
        setAudits(auditRows);
        setNotes(res.data.ClaimNotes);
        setClaimStatus(res.data.Status ?? res.data.ClaimStatus);
        setActive(false);

        setIsInWaitingPeriod(
          Number(res.data.Policy?.OutstandingPremium ?? 0) < 0
        );
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  }, [claimId]);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <TagOutlined
                  style={{
                    paddingRight: "5px",
                    color: "#f04f05",
                  }}
                />
                {claim.ClaimNumber}
              </h2>
              <div className="interactions">
                <Button onClick={handleBack} size="large">
                  <ArrowLeftOutlined style={{ paddingRight: "5px" }} />
                  Back
                </Button>

                <Button onClick={handleOpen} size="large">
                  <BookOutlined style={{ paddingRight: "5px" }} />
                  Create Claim Note
                </Button>

                <Modal
                  title="Create Claim Note"
                  open={isModalOpen}
                  onOk={() => handleAddNote(claimId, noteContent)}
                  onCancel={handleCancel}
                  okText="Send"
                  cancelText="Cancel"
                >
                  <Input.TextArea
                    rows={4}
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Write your Note content here..."
                  />
                </Modal>
              </div>
              <Tabs
                type="card"
                defaultActiveKey="1"
                items={items}
                onChange={onChange}
              />

              {state.approve && (
                <ApproveClaimModal
                  show={state.approve}
                  claimId={claimId}
                  showModal={showApproveModal}
                  handleAdd={handleApproveClaim}
                />
              )}

              {state.reject && (
                <RejectClaimModal
                  show={state.reject}
                  claimId={claimId}
                  showModal={showRejectModal}
                  handleAdd={handleRejectClaim}
                />
              )}

              {state.paid && (
                <PaidClaimModal
                  show={state.paid}
                  claimId={claimId}
                  showModal={showPaidModal}
                  handleAdd={handlePaidClaim}
                />
              )}

              {state.close && (
                <CloseClaimModal
                  show={state.close}
                  claimId={claimId}
                  showModal={showCloseModal}
                  handleAdd={handleCloseClaim}
                />
              )}

              <AddClaimDocumentModal
                show={showUploadDocs}
                showModal={() => setShowUploadDocs((v) => !v)}
                handleAdd={handleAddClaimDocuments}
              />
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default ClaimManagementPage;
